export const checkAllInputsFilled = (documentTypeDocumentData) => {
    const platformValue = documentTypeDocumentData.find(
        (field) => field.fieldCode === 'PLATFORM'
    )?.value?.toLowerCase()
    return documentTypeDocumentData.every((field) => {
        if (platformValue === 'lyft' && field.fieldCode === 'CATEGORY') {
            return true
        }
        return field.value && field.value.toString().trim() !== ""
    })
}

const normalizeMediaPath = (value = "") => (value || "").toLowerCase().split("?")[0].split("#")[0]

export const isImage = (url = "", name = "") => {
    const regex = /\.(jpg|jpeg|png|gif|bmp)$/i
    return regex.test(url || "") || regex.test(name || "")
}

export const isPdf = (url = "", name = "") => {
    const lowerUrl = normalizeMediaPath(url)
    const lowerName = normalizeMediaPath(name)
    return lowerUrl.includes(".pdf") || lowerName.endsWith(".pdf")
}

export const isVideo = (url = "", name = "", mimeType = "") => {
    const videoExtRegex = /\.(mp4|mov|m4v|webm|ogg|ogv)$/i
    const rawUrl = (url || "").toLowerCase()
    const normalizedUrl = normalizeMediaPath(url)
    const normalizedName = normalizeMediaPath(name)
    const normalizedMimeType = (mimeType || "").toLowerCase()
    return (
        videoExtRegex.test(normalizedUrl) ||
        videoExtRegex.test(normalizedName) ||
        normalizedMimeType.startsWith("video/") ||
        normalizedMimeType.includes("quicktime") ||
        rawUrl.includes("video/") ||
        rawUrl.includes("video%2f") ||
        rawUrl.includes("quicktime") ||
        normalizedUrl.startsWith("data:video/")
    )
}

export const getFileKind = (file) => {
    const type = (file?.type || "").toLowerCase()
    const name = (file?.name || "").toLowerCase()
    if (type.startsWith("video/")) return "video"
    if (type.startsWith("image/")) return "image"
    if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf"
    return "file"
}

export const getFileIconClass = (file) => {
    const kind = getFileKind(file)
    if (kind === "pdf") return "fa fa-file-pdf-o file-kind-icon is-pdf"
    if (kind === "video") return "fa fa-film file-kind-icon is-video"
    if (kind === "image") return "fa fa-file-image-o file-kind-icon is-image"
    return "fa fa-file-o file-kind-icon"
}

export const isSelectField = (fieldCode) => {
    if (!fieldCode) return false
    const prefixes = ["COUNTRY", "STATE", "CITY", "GENDER", "PLATFORM", "CATEGORY"]
    return prefixes.some((prefix) => fieldCode.startsWith(prefix))
}

export const isLinkField = (fieldCode) => {
    if (!fieldCode) return false
    return LINK_FIELD_VARIANTS.includes(fieldCode)
}

export const isValidUrl = (value) => {
    if (typeof value !== 'string') return false
    const trimmed = value.trim()
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}([/\w\-._~:?#[\]@!$&'()*+,;=%]*)?$/i
    return urlPattern.test(trimmed)
}

export const checkAllInputsValid = (documentTypeDocumentData) => {
    return Array.isArray(documentTypeDocumentData) &&
        documentTypeDocumentData.every((field) => !field.errorMessage || field.errorMessage === "")
}

const ZIPCODE_VARIANTS = [
    'ZIPCODE',
    'ZIPCODE_MAIN',
    'ZIPCODE_TAX',
    'ZIPCODE_PARKING',
    'ZIPCODE_ID',
    'ZIPCODE_SEC'
]

const LINK_FIELD_VARIANTS = [
    'ADDRESS_LINK',
    'VALIDATION_LINK',
    'PARKING_LINK',
    'ADDITIONAL_ADDRS_LINK'
]

const DOCUMENT_FIELD_RULES = {
    // Document-specific rules
    'Constancia de Situacion Fiscal': {
        'VALUE': {
            minLength: 13,
            maxLength: 13,
            removeSpaces: true,
            toUpperCase: true,
            alphanumeric: true,
        },
        'CURP': {
            minLength: 18,
            maxLength: 18,
            removeSpaces: true,
            toUpperCase: true,
            alphanumeric: true
        },
        'FULL_NAME_CSF': {
            lettersOnly: true,
            maxLength: 70
        },
    },
    'CURP': {
        'VALUE': {
            minLength: 18,
            maxLength: 18,
            removeSpaces: true,
            toUpperCase: true,
            alphanumeric: true
        }
    },
    'Driver License': {
        'VALUE': {
            minLength: 7,
            maxLength: 13,
            removeSpaces: true,
            toUpperCase: true,
            alphanumeric: true
        },
    },
    // Global field rules (apply to any document type)
    '*': {
        'FIRST_NAME': {
            lettersOnly: true,
            maxLength: 30
        },
        'LAST_NAME': {
            lettersOnly: true,
            maxLength: 30
        },
        'TRIPS': {
            numbersOnly: true,
            maxLength: 6
        },
        'RATING': {
            allowNumbersAndDot: true,
            maxLength: 4
        },
        'YEARS_ON_PLATFORM': {
            numbersWithDecimal: true,
            maxLength: 3
        },
        ...Object.fromEntries(
            ZIPCODE_VARIANTS.map(zipField => [zipField, {
                numbersOnly: true,
                minLength: 5,
                maxLength: 5
            }])
        ),
        ...Object.fromEntries(
            LINK_FIELD_VARIANTS.map(linkField => [linkField, {
                url: true,
                maxLength: 2048
            }])
        ),
        // Default rules for all other fields
        '*': {
            alphanumericWithSpaces: true,
        }
    }
}

/**
 * Applies formatting and validation rules to a field value based on metadata.
 *
 * @param {string} value - (Required) Raw input value to sanitize and validate.
 * @param {string} documentTypeName - (Optional) Document type (e.g., "DRIVER_LICENSE"). Required only for document-specific rules.
 * @param {string} fieldName - (Required) Field code (e.g., "FIRST_NAME", "DATE_OF_BIRTH") used to look up rules.
 * @param {string} fieldType - (Optional) Field input type (e.g., "TEXT", "DATE"). Required for type-specific validations (e.g., date logic).
 * @param {Array<Object>} documentTypeDocumentData - (Optional) Array of field objects with `fieldCode` and `value`. Needed only for cross-field validation.
 *
 * @returns {{ processedValue: string, errorMessage: string }} Processed value and any validation error message.
 *
 * @example
 * applyFieldRules("Carlos123", "", "FIRST_NAME", "TEXT", [])
 * // => { processedValue: "Carlos", errorMessage: "" }
 */
export function applyFieldRules(value, documentTypeName, fieldName, fieldType, documentTypeDocumentData) {
    if (!value || isSelectField(fieldName)) return { processedValue: value, errorMessage: '' }
    let processedValue = value
    let errorMessage = ''

    if (fieldType?.toUpperCase() === 'DATE') {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (fieldName === 'ISSUED_DATE') {
            const date = new Date(processedValue)
            if (date > today) {
                errorMessage = "Issued date can't be in the future."
            } else if (today.getFullYear() - date.getFullYear() > 60) {
                errorMessage = "Issued date can't be older than 60 years."
            }
        } else if (fieldName === 'EXPIRATION_DATE') {
            const expirationDate = new Date(processedValue)
            const issuedDate = documentTypeDocumentData.find((field) => field.fieldCode === 'ISSUED_DATE')
            const issuedDateValue = issuedDate ? new Date(issuedDate.value) : null
            expirationDate.setHours(0, 0, 0, 0)
            issuedDateValue.setHours(0, 0, 0, 0)
            if (expirationDate < issuedDateValue) {
                errorMessage = "Expiration date must be after issued date."
            } else if (expirationDate < today) {
                errorMessage = "Expiration date can't be in the past."
            }
        } else if (fieldName === 'DATE_OF_BIRTH') {
            const dob = new Date(processedValue)
            if (dob > today) {
                errorMessage = "Date of birth can't be in the future."
            } else if (today.getFullYear() - dob.getFullYear() > 80) {
                errorMessage = "Date of birth can't be older than 80 years."
            }
        } else if (fieldName === 'ANTIQUITY') {
            const date = new Date(processedValue)
            if (date > today) {
                errorMessage = "Antiquity date can't be in the future."
            } else if (today.getFullYear() - date.getFullYear() > 60) {
                errorMessage = "Antiquity date can't be older than 60 years."
            }
        }
        if (errorMessage) {
            return { processedValue, errorMessage }
        }
        // If no error, format the date to YYYY-MM-DD
        const formattedDate = new Date(processedValue).toISOString().split('T')[0]
        processedValue = formattedDate
        return { processedValue, errorMessage }
    }

    // Get rules
    const globalDefaultRules = DOCUMENT_FIELD_RULES['*']?.['*'] || {}
    const globalFieldRules = DOCUMENT_FIELD_RULES['*']?.[fieldName] || {}
    const docSpecificRules = DOCUMENT_FIELD_RULES[documentTypeName]?.[fieldName] || {}

    // if no specific rules are found, use global rules
    const rules =
        Object.keys(docSpecificRules).length > 0
            ? docSpecificRules
            : Object.keys(globalFieldRules).length > 0
                ? globalFieldRules
                : globalDefaultRules

    // Apply transformations in sequence
    if (rules.removeSpaces) {
        processedValue = processedValue.replace(/\s/g, '')
    }

    if (rules.toUpperCase) {
        processedValue = processedValue.toUpperCase()
    }

    if (rules.numbersOnly) {
        processedValue = processedValue.replace(/[^0-9]/g, '')
    }

    if (rules.numbersWithDecimal) {
        processedValue = processedValue.replace(/[^0-9.]/g, '')
    }

    if (rules.allowNumbersAndDot) {
        processedValue = processedValue.replace(/^(?!^$)(\d+(\.\d{0,2})?)?.*$/, '$1')
    }

    if (rules.lettersOnly) {
        processedValue = processedValue.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ´\s]/g, '')
    }

    if (rules.alphanumericWithSpaces) {
        processedValue = processedValue.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ´\s]/g, '')
    }

    if (rules.alphanumeric) {
        processedValue = processedValue.replace(/[^a-zA-Z0-9]/g, '')
    }

    if (rules.maxLength) {
        processedValue = processedValue.slice(0, rules.maxLength)
    }

    if (rules.minLength && processedValue.length < rules.minLength) {
        errorMessage = `Minimum length is ${rules.minLength}`
    }

    if (rules.url && typeof processedValue === 'string') {
        processedValue = processedValue.trim()

        if (!isValidUrl(processedValue)) {
            errorMessage = "Please enter a valid URL."
        }
    }

    return { processedValue, errorMessage }
}

export const handleDriverDataChange = (
    input,
    documentTypeDocumentData,
    documentTypeName,
    updateState,
) => {
    if (!input) return
    let { name, value } = input.target || input
    const fieldType = documentTypeDocumentData.find((field) => field.fieldCode === name)?.fieldType

    let updatedData = [...documentTypeDocumentData]

    const clearFields = (prefix) => {
        updatedData = updatedData.map((field) =>
            field.fieldCode.startsWith(prefix)
                ? { ...field, value: "" }
                : field
        )
    }

    if (name.startsWith("COUNTRY")) {
        clearFields("STATE")
        clearFields("CITY")
    } else if (name.startsWith("STATE")) {
        clearFields("CITY")
    }

    const { processedValue, errorMessage } = applyFieldRules(
        value,
        documentTypeName,
        name,
        fieldType,
        updatedData
    )

    updatedData = updatedData.map((field) =>
        field.fieldCode === name
            ? { ...field, value: processedValue, errorMessage }
            : field
    )

    // Call the state updater with the new data
    updateState(updatedData)
}

export const categoryNameMapping = {
    ID: "ID Information",
    DRIVER: "Driver Information",
    ADDRESS: "Address Information",
}

export const groupFieldsByCategory = (fields) => {
    if (!fields || fields.length === 0) return {}
    return fields.reduce((acc, field) => {
        if (!acc[field.dataCategory]) {
            acc[field.dataCategory] = []
        }
        acc[field.dataCategory].push(field)
        return acc
    }, {})
}

export const hasIssuedAndExpirationDates = (field) => {
    return field.fieldCode === "ISSUED_DATE" || field.fieldCode === "EXPIRATION_DATE"
}

export const validateDocumentData = (driverDocumentData, selectedDocumentType) => {
    if (!driverDocumentData || !driverDocumentData.documentData || !selectedDocumentType) {
        return []
    }

    for (const field of driverDocumentData.documentData) {
        if (field.documentName === selectedDocumentType.typeName) {
            return field.requiredData || []
        }
    }

    return []
};

/**
 * Prepare a stored document url for display.
 *
 * Lives here rather than in DocumentsListNew so that callers who only need the url
 * cleaned do not have to import that module -- it pulls in the whole driver/car
 * document modal tree with it. DocumentsListNew re-exports this for its existing
 * importers.
 */
export const cleanDocumentUrl = (url) => {
    if (!url) return ""
    url = url.replace('%2F', '/')
    url = url.replace('&export=download', '')
    url = url.replace('https://storage.googleapis.com/download/storage/v1/b/buggy_files/o', 'https://storage.googleapis.com/buggy_files')
    // Remove everything starting from ?generation= until the end of the string
    url = url.replace(/\?generation=.*/g, '')
    return url
}
